import React, { useEffect, useRef, useState } from 'react'
import { ApolloError } from '@apollo/client'

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/solid'
import { useRetryRecordCreationMutation } from '../../graphql/graphql'
import {
  RETRY_RECORD_CREATION_ERROR_MSG,
  RETRY_RECORD_CREATION_EXCLUDED_DOMAIN
} from '../../constants/constants'
import styles from './RetryRecordCreation.module.scss'

const TOAST_DURATION_MS = 6000

export const isRetryRecordCreationEligible = (profile: {
  id?: string
  email?: string
  salesforce_id?: string | null
}) => {
  const domain = (profile.email?.split('@')[1] ?? '').toLowerCase()
  const isExcludedDomain =
    domain === RETRY_RECORD_CREATION_EXCLUDED_DOMAIN ||
    domain.endsWith(`.${RETRY_RECORD_CREATION_EXCLUDED_DOMAIN}`)

  return (
    Boolean(profile.id) && !profile.salesforce_id?.trim() && !isExcludedDomain
  )
}

const errorMessage = (err: unknown) =>
  (err instanceof ApolloError && err.graphQLErrors[0]?.message) ||
  RETRY_RECORD_CREATION_ERROR_MSG

export function RetryRecordCreation(props: {
  profile: { id?: string; email?: string; salesforce_id?: string | null }
  onSuccess: (userId: string, salesforceId: string) => void
}) {
  const { profile, onSuccess } = props
  const userId = profile.id ?? ''
  const [retryMutation, { loading }] = useRetryRecordCreationMutation()
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  const [announcement, setAnnouncement] = useState('')

  const currentUserId = useRef(userId)

  useEffect(() => {
    currentUserId.current = userId
    setError('')
    setAnnouncement('')
  }, [userId])

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(''), TOAST_DURATION_MS)
    return () => clearTimeout(timer)
  }, [toast])

  const handleRetry = async () => {
    setError('')
    setAnnouncement('Retrying record creation. This may take up to a minute.')

    try {
      const { data } = await retryMutation({ variables: { id: userId } })
      if (currentUserId.current !== userId) return
      const salesforceId = data?.retryRecordCreation?.salesforceId

      if (!salesforceId) {
        setAnnouncement('')
        setError(RETRY_RECORD_CREATION_ERROR_MSG)
        return
      }

      onSuccess(userId, salesforceId)
      const message = `Salesforce record created successfully. Salesforce Id: ${salesforceId}`
      setToast(message)
      setAnnouncement(message)
    } catch (err) {
      if (currentUserId.current !== userId) return
      setAnnouncement('')
      setError(errorMessage(err))
    }
  }

  const eligible = isRetryRecordCreationEligible(profile)

  return (
    <>
      <div className={styles.srOnly} role="status" aria-live="polite">
        {announcement}
      </div>
      {eligible && (
        <div className={styles.warningBox} data-testid="retry-record-creation">
          <ExclamationTriangleIcon
            className={styles.warningIcon}
            aria-hidden="true"
          />
          <div className={styles.warningText}>
            <strong>This profile is missing a Salesforce Id.</strong> Trial
            provisioning, billing and entitlement sync may be blocked until a
            Lead/Contact is created.
          </div>
          <button
            type="button"
            className={`btn btn-primary ${styles.retryButton}`}
            onClick={handleRetry}
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                Retrying...
              </>
            ) : (
              'Retry Lead / Contact Creation'
            )}
          </button>
        </div>
      )}
      {error && (
        <div className={styles.errorBanner} role="alert">
          <span>{error}</span>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Dismiss"
            onClick={() => setError('')}
          >
            <XMarkIcon aria-hidden="true" />
          </button>
        </div>
      )}
      {toast && (
        <div className={styles.toast}>
          <CheckCircleIcon className={styles.toastIcon} aria-hidden="true" />
          <span>{toast}</span>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Dismiss"
            onClick={() => setToast('')}
          >
            <XMarkIcon aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  )
}
